import * as React from "react"
import { cn } from "@/lib/utils"

const spinnerSizes = {
  sm: "h-4 w-4",
  md: "h-6 w-6",
  lg: "h-8 w-8",
  xl: "h-12 w-12",
} as const

export interface LoaderSpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: keyof typeof spinnerSizes
}

/**
 * Consistent spinner used across the app (buttons, inline, overlays).
 */
export function LoaderSpinner({ size = "lg", className, ...props }: LoaderSpinnerProps) {
  return (
    <div
      role="status"
      aria-label="Loading"
      className={cn(
        "animate-spin rounded-full border-b-2 border-primary",
        spinnerSizes[size],
        className
      )}
      {...props}
    />
  )
}

function Spinner({ size = "lg", className }: { size?: keyof typeof spinnerSizes; className?: string }) {
  return (
    <LoaderSpinner size={size} className={className} />
  )
}

export interface PageLoaderProps {
  /** Optional message below the spinner (e.g. "Loading schedule...") */
  message?: React.ReactNode
  /** Spinner size */
  size?: "sm" | "md" | "lg" | "xl"
  /** Use min-h-screen for full-page loading */
  fullScreen?: boolean
  /** Fixed overlay with backdrop (for modal-style loading) */
  overlay?: boolean
  /** Minimum height when not fullScreen (e.g. "min-h-[400px]") */
  minHeight?: string
  /** Layout: "centered" (stacked spinner + message) or "inline" (horizontal, for nav/toolbar) */
  layout?: "centered" | "inline"
  className?: string
}

/**
 * Consistent page/section loader: centered spinner + optional message.
 * Use for initial page load, table loading, and overlay loading states.
 */
export function PageLoader({
  message,
  size = "lg",
  fullScreen,
  overlay,
  minHeight = "min-h-[200px]",
  layout = "centered",
  className,
}: PageLoaderProps) {
  const content =
    layout === "inline" ? (
      <div className="flex items-center gap-2">
        <Spinner size={size} className="flex-shrink-0" />
        {message != null && <span className="text-muted-foreground">{message}</span>}
      </div>
    ) : (
      <div className="text-center">
        <Spinner size={size} className="mx-auto mb-4" />
        {message != null && <p className="mt-2 text-muted-foreground">{message}</p>}
      </div>
    )

  const wrapperClass = cn(
    "flex items-center justify-center",
    fullScreen && "min-h-screen",
    !fullScreen && !overlay && minHeight,
    overlay && "fixed inset-0 z-50 bg-white/70",
    className
  )

  return <div className={wrapperClass}>{content}</div>
}
